import React, { FC } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Upload,
} from "antd";
import { CheckCircleOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";
import {
  FHPL_ADMISSION_TYPE_OPTIONS,
  FHPL_ANY_OTHER_DISEASE_OPTIONS,
  FHPL_CLASS_OF_ACCOMMODATION_OPTIONS,
  FHPL_DATE_FORMAT,
  FHPL_DURATION_UNITS,
  FHPL_GENDER_OPTIONS,
  FHPL_PROPOSED_LINE_OF_TREATMENT_OPTIONS,
  FHPL_RELATIONSHIP_OPTIONS,
  FHPL_ROUTE_OF_ADMINISTRATION_OPTIONS,
} from "../../../../apis/SchemeService/FHPL/fhplService";

type PreAuthFhplProps = {
  form: any;
  handleSaveConfirmationModal: (
    state: "save" | "update" | "intimate" | "approve" | "initiate" | "submit"
  ) => void;
  handleSubmit?: () => void;
  isSaving: boolean;
  isIntimating: boolean;
  isApproving: boolean;
  isSubmitting?: boolean;
  integratedWithPortal?: boolean;
  isApproved: boolean;
  isSubmitted: boolean;
  isInitiated: boolean;
  isIntimated: boolean;
  hasExistingRecord: boolean;
  documentRequestResponseDocuments: any;
  documentUrls: any[];
  handleFileChange: (documentType: string, isMultiple: boolean) => (e: any) => void;
  handleOpen: (docType: any, documentUrl: any) => void;
  uploadFileList: any[];
  showVerifyButton?: boolean;
  isValidationLoading?: boolean;
  registrationValidationStatus?: any;
  handleClaimValidation?: () => void;
};

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 15,
  marginBottom: 12,
  marginTop: 8,
  color: "#004c8f",
};

const readonlyFieldStyle: React.CSSProperties = {
  backgroundColor: "#f0f0f0",
  color: "#595959",
};

const requiredRule = (message: string) => ({ required: true, message });

const mobileTenDigitRules = [
  requiredRule("Please enter mobile number"),
  { pattern: /^\d{10}$/, message: "Mobile number must be exactly 10 digits" },
];

const optionalMobileRules = [
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (!/^\d{10}$/.test(value)) {
        return Promise.reject(new Error("Mobile number must be exactly 10 digits"));
      }
      return Promise.resolve();
    },
  },
];

const decimalAmountMessage =
  "Only numeric values with up to 2 decimal places are allowed";

const decimalAmountRule = {
  pattern: /^(\d+(\.\d{1,2})?|\.\d{1,2})$/,
  message: decimalAmountMessage,
};

const optionalDecimalRules = [
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(value)) {
        return Promise.reject(new Error(decimalAmountMessage));
      }
      return Promise.resolve();
    },
  },
];

const wholeNumberRules = [
  {
    validator: (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      if (!/^\d{1,3}$/.test(value)) {
        return Promise.reject(new Error("Value should be up to 3 digits only"));
      }
      return Promise.resolve();
    },
  },
];

const yesNoRadio = (disabled: boolean) => (
  <Radio.Group disabled={disabled}>
    <Radio value={true}>Yes</Radio>
    <Radio value={false}>No</Radio>
  </Radio.Group>
);

const durationFieldStyle: React.CSSProperties = {
  borderRadius: 8,
};

const DurationWithUnit: FC<{
  valueName: string | (string | number)[];
  unitName: string | (string | number)[];
  required?: boolean;
  disabled?: boolean;
}> = ({ valueName, unitName, required = false, disabled = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
    }}
  >
    <Form.Item
      name={valueName}
      style={{ flex: 1, marginBottom: 0, minWidth: 0 }}
      rules={required ? [requiredRule("Please enter duration")] : wholeNumberRules}
    >
      <Input
        placeholder="0"
        inputMode="numeric"
        disabled={disabled}
        style={{ ...durationFieldStyle, width: "100%" }}
      />
    </Form.Item>
    <Form.Item
      name={unitName}
      initialValue="Days"
      style={{ width: 108, marginBottom: 0, flexShrink: 0 }}
    >
      <Select
        disabled={disabled}
        style={{ ...durationFieldStyle, width: "100%" }}
        popupMatchSelectWidth
      >
        {FHPL_DURATION_UNITS.map((unit) => (
          <Select.Option key={unit} value={unit}>
            {unit.toUpperCase()}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  </div>
);

const PastHistoryDurationField: FC<{
  label: string;
  valueName: string | (string | number)[];
  unitName: string | (string | number)[];
  required?: boolean;
  disabled?: boolean;
}> = ({ label, valueName, unitName, required = false, disabled = false }) => (
  <Form.Item
    label={
      <>
        {label}
        {required ? <span style={{ color: "red" }}> *</span> : null}
      </>
    }
  >
    <DurationWithUnit
      valueName={valueName}
      unitName={unitName}
      required={required}
      disabled={disabled}
    />
  </Form.Item>
);

const PreAuthFhpl: FC<PreAuthFhplProps> = ({
  form,
  handleSaveConfirmationModal,
  handleSubmit,
  isSaving,
  isIntimating,
  isApproving,
  isSubmitting = false,
  integratedWithPortal = true,
  isApproved,
  isSubmitted,
  isInitiated,
  isIntimated,
  hasExistingRecord,
  documentRequestResponseDocuments,
  documentUrls,
  handleFileChange,
  handleOpen,
  uploadFileList,
  showVerifyButton,
  isValidationLoading,
  registrationValidationStatus,
  handleClaimValidation,
}) => {
  const { auth } = useSelector((state: RootState) => state);
  const { hasFunctionality } = useUserFunctionalities();
  const actionButtonStyle = {
    backgroundColor: "rgb(0, 76, 143)",
    color: "#fff",
    boxShadow: "0px 2px 8px 0px #00000040",
    width: "100%",
  };

  const renderDocumentRows = (type: "requestedDocuments" | "responseDocuments") => {
    const docs = documentRequestResponseDocuments?.[type] || [];
    return docs?.map((docType: any) => {
      const matchingDocuments = documentUrls?.filter(
        (doc) => doc?.documentLabelType === docType?.label
      );

      return (
        <div
          key={`${type}-${docType?.id}`}
          style={{
            border: "1px solid #eee",
            borderRadius: 8,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Upload
              multiple={Boolean(docType?.multiple)}
              showUploadList={false}
              beforeUpload={() => false}
              {...(docType?.multiple ? { fileList: uploadFileList } : {})}
              onChange={handleFileChange(docType?.labelId, Boolean(docType?.multiple))}
              disabled={
                (auth?.role?.role === "FO_STAFF" && isIntimated) || isSubmitted
              }
            >
              <Button
                icon={<UploadOutlined />}
                disabled={
                  (auth?.role?.role === "FO_STAFF" && isIntimated) ||
                  isSubmitted
                }
              />
            </Upload>
            <span>
              {docType?.label}
              {docType?.required ? <span style={{ color: "red" }}> *</span> : null}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {matchingDocuments?.map((doc: any, idx: number) => (
              <Button
                key={`${doc?.id}-${idx}`}
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleOpen(doc?.documentLabelType, doc?.documentUrl)}
              >
                {idx + 1}
              </Button>
            ))}
          </div>
        </div>
      );
    });
  };

  const patientDetailsSection = (
    <div>
      <div style={sectionTitleStyle}>Patient Details</div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Patient Name"
            name="patientName"
            rules={[requiredRule("Please enter patient name")]}
          >
            <Input disabled style={readonlyFieldStyle} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[requiredRule("Please select gender")]}
          >
            <Select placeholder="Select" allowClear>
              {FHPL_GENDER_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Relationship"
            name="relationship"
            rules={[requiredRule("Please select relationship")]}
          >
            <Select placeholder="-- Select --" allowClear>
              {FHPL_RELATIONSHIP_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="DateOfBirth (dd/mm/yyyy)"
            name="dateOfBirth"
            rules={[requiredRule("Please select date of birth")]}
          >
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Form.Item label="Age" required>
            <DurationWithUnit
              valueName="age"
              unitName="ageUnit"
              required
              disabled={isSubmitted}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Patient MobileNo"
            name="patientMobile"
            rules={mobileTenDigitRules}
          >
            <Input
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                form.setFieldValue("patientMobile", digits);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Attending Relative Mobile"
            name="attendingRelativeMobile"
            rules={optionalMobileRules}
          >
            <Input
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                form.setFieldValue("attendingRelativeMobile", digits);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Policy No"
            name="policyNumber"
            rules={[requiredRule("Please enter policy number")]}
          >
            <Input disabled style={readonlyFieldStyle} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Family Physician Name" name="familyPhysicianName">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Physician Mobile"
            name="physicianMobile"
            rules={optionalMobileRules}
          >
            <Input
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                form.setFieldValue("physicianMobile", digits);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Any Other Mediclaim/ Health Insurance"
            name="otherMediclaim"
            initialValue={false}
          >
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Other Insurance Company Name(If yes)" name="otherInsuranceCompanyName">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Other PolicyNo(If yes)" name="otherPolicyNo">
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const hospitalizationSection = (
    <div>
      <div style={sectionTitleStyle}>Hospitalization Details</div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Treating Doctor Name"
            name="treatingDoctorName"
            rules={[requiredRule("Please enter treating doctor name")]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Doctor Mobile"
            name="doctorMobileNo"
            rules={mobileTenDigitRules}
          >
            <Input
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                form.setFieldValue("doctorMobileNo", digits);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Probable Date of Admission (dd/mm/yyyy)"
            name="probableAdmissionDate"
            rules={[requiredRule("Please select probable date of admission")]}
          >
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Class of Accomodation"
            name="classOfAccommodation"
            rules={[requiredRule("Please select class of accommodation")]}
          >
            <Select placeholder="-- Select --" allowClear>
              {FHPL_CLASS_OF_ACCOMMODATION_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Estimated Days"
            name="estimatedDays"
            rules={[
              requiredRule("Please enter estimated days"),
              { pattern: /^\d{1,3}$/, message: "Days should be up to 3 digits only" },
            ]}
          >
            <Input inputMode="numeric" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Date of First Consultation" name="dateOfFirstConsultation">
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Present Complaints"
            name="presentComplaints"
            rules={[requiredRule("Please enter present complaints")]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Form.Item label="Duration Of Ailment">
            <DurationWithUnit
              valueName="ailmentDuration"
              unitName="ailmentDurationUnit"
              disabled={isSubmitted}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            label="Past history of present ailment if any"
            name="pastHistoryPresentAilment"
            rules={[requiredRule("Please enter past history of present ailment")]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Clinical Findings - BP" name="clinicalFindingBp">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Clinical Findings - Pulse Rate" name="clinicalFindingPulseRate">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Clinical Findings - Temperature" name="clinicalFindingTemperature">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Clinical Findings - Cardio Vascular System"
            name="clinicalFindingCardioVascular"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Clinical Findings - Per Abdomen" name="clinicalFindingPerAbdomen">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Clinical Findings - Respiratory Rate"
            name="clinicalFindingRespiratoryRate"
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <div style={sectionTitleStyle}>Diagnosis</div>
      <p style={{ color: "#d46b08", marginBottom: 8 }}>
        Searching diagnosis is now simpler than ever. Please follow the steps. If you have any
        difficulty in selecting Diagnosis please feel free to call on 9951255501
      </p>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            label="Primary Diagnosis/ICD 10 Code (Step 1)"
            name="primaryDiagnosis"
            rules={[requiredRule("Please enter primary diagnosis")]}
          >
            <Input placeholder="Search Diagnosis/ICD 10 Code" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Proposed Line of Treatment (Step 2)"
            name="proposedLineOfTreatment"
            rules={[requiredRule("Please select proposed line of treatment")]}
          >
            <Select placeholder="-- Select --" allowClear>
              {FHPL_PROPOSED_LINE_OF_TREATMENT_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Name of the surgery/Medical Management(Level3) (Step 3)"
            name="surgeryOrMedicalManagement"
            rules={[requiredRule("Please enter surgery/medical management")]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Investigations" name="investigations">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="ICD 10 PCS Code" name="icd10PcsCode">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="ICD 10 Code" name="icd10Code">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Route of Administration"
            name="routeOfAdministration"
            rules={[requiredRule("Please select route of administration")]}
          >
            <Select placeholder="-Select-" allowClear>
              {FHPL_ROUTE_OF_ADMINISTRATION_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Admission Type"
            name="admissionType"
            rules={[requiredRule("Please select admission type")]}
          >
            <Select placeholder="-- Select --" allowClear>
              {FHPL_ADMISSION_TYPE_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="No. Of Sessions" name="noOfSessions" rules={wholeNumberRules}>
            <Input inputMode="numeric" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Clinical Findings - Others" name="clinicalFindingsOthers">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="In Case of Accident (Is it RTA)" name="isRta" initialValue={false}>
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="How Did Injury Occur" name="howDidInjuryOccur">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Date of Injury (dd/mm/yyyy)" name="dateOfInjury">
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Reported To Police"
            name="reportedToPolice"
            initialValue={false}
          >
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="FIR NO" name="firNo">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Injury/Disease Caused due to substance abuse / alcohol consumption"
            name="substanceAbuse"
            initialValue={false}
          >
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Test Conducted to Establish this"
            name="testConducted"
            initialValue={false}
          >
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="In Case of Maternity" name="isMaternity" initialValue={false}>
            {yesNoRadio(isSubmitted)}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Obstetric History (G P L A D)">
            <Input.Group compact style={{ display: "flex" }}>
              {(["obstetricG", "obstetricP", "obstetricL", "obstetricA", "obstetricD"] as const).map(
                (name, index) => (
                  <Form.Item key={name} name={name} style={{ width: "20%", marginBottom: 0 }}>
                    <Input
                      placeholder={["G", "P", "L", "A", "D"][index]}
                      inputMode="numeric"
                    />
                  </Form.Item>
                )
              )}
            </Input.Group>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="LMP(dd/mm/yyyy)" name="lmp">
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Date of Delivery(dd/mm/yyyy)" name="dateOfDelivery">
            <DatePicker format={FHPL_DATE_FORMAT} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const pastHistoryBillingSection = (
    <div>
      <div style={sectionTitleStyle}>Past History</div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Hypertension"
            valueName={["hypertension", "value"]}
            unitName={["hypertension", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Diabetes Mellitus"
            valueName={["diabetesMellitus", "value"]}
            unitName={["diabetesMellitus", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Ischemic Heart Disease"
            valueName={["ischemicHeartDisease", "value"]}
            unitName={["ischemicHeartDisease", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="COPD/Br Asthama"
            valueName={["copdBronchialAsthma", "value"]}
            unitName={["copdBronchialAsthma", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Cancer"
            valueName={["cancer", "value"]}
            unitName={["cancer", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Alcoholism"
            valueName={["alcoholism", "value"]}
            unitName={["alcoholism", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Drug abuse"
            valueName={["drugAbuse", "value"]}
            unitName={["drugAbuse", "unit"]}
            required
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="HIV or STD Related"
            valueName={["hivOrStd", "value"]}
            unitName={["hivOrStd", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Smoking"
            valueName={["smoking", "value"]}
            unitName={["smoking", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Tobacco Chewing"
            valueName={["tobaccoChewing", "value"]}
            unitName={["tobaccoChewing", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Ghutka Chewing"
            valueName={["ghutkaChewing", "value"]}
            unitName={["ghutkaChewing", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="HyperLipidemias"
            valueName={["hyperlipidemias", "value"]}
            unitName={["hyperlipidemias", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <PastHistoryDurationField
            label="Osteoarthritis"
            valueName={["osteoarthritis", "value"]}
            unitName={["osteoarthritis", "unit"]}
            disabled={isSubmitted}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Form.Item
            label="Any Other Disease(If Yes, give details)"
            name="anyOtherDisease"
            rules={[requiredRule("Please select any other disease")]}
          >
            <Select placeholder="Select" allowClear>
              {FHPL_ANY_OTHER_DISEASE_OPTIONS.map((option) => (
                <Select.Option key={option} value={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Form.Item label="Others(If Yes, give details here)" name="pastHistoryOthers">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <div style={sectionTitleStyle}>Billing Information</div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Per Day Room Rent + Nursing & Service Charge + Patient Diet"
            name="perDayRoomRent"
            rules={[
              requiredRule("Please enter per day room rent"),
              decimalAmountRule,
            ]}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Perday ICU Charges" name="perDayIcuCharges" rules={optionalDecimalRules}>
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Investigations Charge" name="investigationsCharge" rules={optionalDecimalRules}>
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Consultation Charges"
            name="consultationCharges"
            rules={[
              requiredRule("Please enter consultation charges"),
              decimalAmountRule,
            ]}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Medicines + Consumables Other hospital Expenses if any"
            name="medicinesConsumables"
            rules={[
              requiredRule("Please enter medicines/consumables charges"),
              decimalAmountRule,
            ]}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="All inclusive packege chages if any applicable"
            name="allInclusivePackage"
            rules={optionalDecimalRules}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Professional fee Surgeon + Asst Surgeon fee + Anesthetist Fees"
            name="professionalFeeSurgeon"
            rules={optionalDecimalRules}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="OT Charges" name="otCharges" rules={optionalDecimalRules}>
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Cost of Implents (if applicable please specify)"
            name="costOfImplants"
            rules={optionalDecimalRules}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Total Estimated Cost"
            name="totalEstimatedCost"
            rules={[
              requiredRule("Please enter total estimated cost"),
              decimalAmountRule,
            ]}
          >
            <Input inputMode="decimal" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ margin: "0px 0.5rem" }}>
      <Form form={form} layout="vertical" disabled={isSubmitted}>
        {patientDetailsSection}
        {hospitalizationSection}
        {pastHistoryBillingSection}

        <h4 style={{ marginTop: 12 }}>Request Document Upload</h4>
        {renderDocumentRows("requestedDocuments")}
        <h4 style={{ marginTop: 12 }}>Response Document Upload</h4>
        {renderDocumentRows("responseDocuments")}

        <div
          style={{
            marginTop: 20,
            borderTop: "1px solid #D9D9D9",
            padding: "15px 20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "30px",
          }}
        >
          {!isSubmitted ? (
            <>
              {hasExistingRecord &&
              !isInitiated &&
              !(auth?.role?.role === "FO_STAFF" && isIntimated) ? (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={() => handleSaveConfirmationModal("update")}
                    loading={isSaving}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    UPDATE
                  </Button>
                </div>
              ) : (
                !hasExistingRecord && (
                  <div style={{ width: "16%" }}>
                    <Button
                      type="primary"
                      onClick={() => handleSaveConfirmationModal("save")}
                      loading={isSaving}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      SAVE
                    </Button>
                  </div>
                )
              )}

              {hasFunctionality("casePreAuthIntimate") &&
                !isIntimated &&
                !isInitiated &&
                !isApproved && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("intimate")}
                      loading={isIntimating}
                      disabled={isSubmitted}
                      style={actionButtonStyle}
                    >
                      Send To QC
                    </Button>
                  </div>
                )}

              {hasFunctionality("preAuthApprover") &&
                !isInitiated &&
                !isApproved &&
                !isSubmitted && (
                  <div style={{ width: "16%" }}>
                    <Button
                      onClick={() => handleSaveConfirmationModal("approve")}
                      loading={isApproving}
                      disabled={isSubmitted || isApproved}
                      style={actionButtonStyle}
                    >
                      QC Approve
                    </Button>
                  </div>
                )}

              {hasFunctionality("casePreAuthSubmission") && !isInitiated && !isSubmitted && (
                <div style={{ width: "16%" }}>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitted}
                    style={actionButtonStyle}
                  >
                    {integratedWithPortal ? "Initiate" : "Submit Manually"}
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Form>

      {isSubmitted && showVerifyButton && handleClaimValidation ? (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={isValidationLoading}
            onClick={handleClaimValidation}
            style={{
              backgroundColor: "rgb(0, 76, 143)",
              color: "#fff",
              boxShadow: "0px 2px 8px 0px #00000040",
              minWidth: 140,
            }}
          >
            {registrationValidationStatus ? "View" : "Verify"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PreAuthFhpl);
